import { HUD } from '../../config/tuning';
import type { Projector, ScreenPoint } from '../../render/Camera';
import { createScreenPoint } from '../../render/Camera';
import type { HudPainter } from '../HudLayout';
import type { HudDegradation } from '../HudDegradation';
import type { HudFrame } from '../HudFrame';

/**
 * The flight path marker (SPEC §3.4.1) — a small circle with two stub wings and a fin, floating
 * at wherever the aircraft is actually going.
 *
 * It is drawn by projecting the *world velocity vector* through the camera, not by converting α
 * and β into an offset. Projection is what makes it correct under bank, under the 4° install
 * tilt, and under look-around, all at once; an α/β offset would be right only in wings-level
 * flight, which is the one case where the marker matters least.
 *
 * The gap between this and the fixed centre reticle is the angle of attack. When it drifts below
 * the reticle in a climb, the aircraft is going somewhere other than where it is pointing — and
 * that reading is the difference between a turn and a mush.
 */

const projected: ScreenPoint = createScreenPoint();

export function drawFpm(
  painter: HudPainter,
  frame: HudFrame,
  projector: Projector,
  degradation: HudDegradation,
): void {
  if (!frame.velocityValid) return;
  if (!projector.projectDirection(frame.velocityDirection, projected)) return;

  const layout = painter.layout;
  const ctx = painter.ctx;

  // Under jamming the marker jitters rather than lying still (SPEC §3.4.2).
  const x = projected.x + layout.px(degradation.fpmJitterX());
  const y = projected.y + layout.px(degradation.fpmJitterY());

  // Off-screen the marker is pinned to the edge: a marker that has left the display is exactly
  // when the player most needs to know which way the aircraft is actually going.
  const margin = layout.px(HUD.marginPx);
  const clampedX = clampRange(x, margin, layout.widthPx - margin);
  const clampedY = clampRange(y, margin, layout.heightPx - margin);
  const pinned = clampedX !== x || clampedY !== y;

  const radius = layout.px(HUD.fpmSizePx) * 0.5;
  const wing = layout.px(HUD.fpmWingPx);
  const stem = layout.px(HUD.fpmStemPx);

  ctx.save();
  ctx.translate(clampedX, clampedY);

  painter.circle(0, 0, radius);

  ctx.beginPath();
  ctx.moveTo(-radius - wing, 0);
  ctx.lineTo(-radius, 0);
  ctx.moveTo(radius, 0);
  ctx.lineTo(radius + wing, 0);
  ctx.moveTo(0, -radius);
  ctx.lineTo(0, -radius - stem);
  painter.stroke();

  if (pinned) {
    // A cross through the marker says "this is a clamped bearing, not a position".
    ctx.beginPath();
    ctx.moveTo(-radius * 0.7, -radius * 0.7);
    ctx.lineTo(radius * 0.7, radius * 0.7);
    ctx.moveTo(radius * 0.7, -radius * 0.7);
    ctx.lineTo(-radius * 0.7, radius * 0.7);
    painter.stroke();
  }

  ctx.restore();
}

function clampRange(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
